import { ContactMigrationResult, ContactMigrationSummary } from '../api/models';
import { TagTone } from './ui';

// A run stays pollable only while the workflow is still executing; every other
// status the server reports is final for this run.
export function isTerminalRunStatus(status: string): boolean {
  return status !== 'running';
}

export function runStatusTone(status: string): TagTone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'running':
      return 'accent';
    case 'failed':
    case 'terminated':
    case 'timedOut':
      return 'danger';
    default:
      return 'neutral';
  }
}

export function resultStatusTone(status: string): TagTone {
  switch (status) {
    case 'verified':
      return 'success';
    case 'shadow':
      return 'accent';
    case 'deferred':
    case 'written':
      return 'warn';
    case 'rolled_back':
    case 'failed':
    case 'error':
      return 'danger';
    default:
      return 'neutral';
  }
}

// The privacy outcome reads as a phrase rather than the wire value: what
// matters at a glance is whether the domain ended up protected, not which
// branch the server took.
export function privacyOutcomeLabel(outcome: string): string {
  switch (outcome) {
    case 'already_on':
      return 'Privacy Already On';
    case 'enabled':
      return 'Privacy Enabled';
    case 'would_enable':
      return 'Would Enable Privacy';
    case 'prohibited_tld':
      return 'TLD Has No Privacy';
    case 'enable_failed':
      return 'Privacy Enable Failed';
    default:
      return outcome;
  }
}

// A prohibited TLD is a warning, not a success: those contacts were written
// without privacy.
export function privacyOutcomeTone(outcome: string): TagTone {
  switch (outcome) {
    case 'already_on':
    case 'enabled':
      return 'success';
    case 'would_enable':
      return 'accent';
    case 'prohibited_tld':
      return 'warn';
    case 'enable_failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

export interface ChangeLabels {
  title: string;
  old: string;
  new: string;
}

// A rolled-back run reuses the change list to report the read-back residue, so
// the two sides are what we wrote against what the registrar came back with —
// not a before and after.
export function changeLabels(status: string): ChangeLabels {
  if (status === 'rolled_back') {
    return { title: 'Unintended Changes', old: 'Wrote', new: 'Read Back' };
  }
  return { title: 'Intended Changes', old: 'Old', new: 'New' };
}

// The server lowercases the targeted domain, so match case-insensitively.
export function resultForDomain(
  summary: ContactMigrationSummary | undefined,
  domain: string,
): ContactMigrationResult | null {
  if (!summary) return null;
  const wanted = domain.trim().toLowerCase();
  return summary.results.find((r) => r.domain.toLowerCase() === wanted) ?? null;
}
